<template>
  <div class="card-product-mapping-root datagrid-root">
    <div class="content-block">
      <div class="headline-container">
        <div class="headline-item"><h2>Stamdata</h2></div>
        <div class="subheadline-item">&nbsp; - Mapning af Card Product Id</div>
      </div>
    </div>
    <dx-file-uploader
      ref="fileUploader"
      :show-file-list="false"
      dialog-trigger="#upload-button"
      accept=".xlsx"
      :visible="false"
      upload-mode="useForm"
      @value-changed="onFileSelected"
    />

    <div class="content-block">
      <div class="supplier-container">
        Supplier:
        <dx-select-box
          :data-source="supplierList.data"
          display-expr="name"
          value-expr="id"
          v-model:value="supplierList.selectedSupplier"
          @value-changed="supplierChanged" />
      </div>
      <div class="grid-button-container">
        <div class="flex-align-right">
          <dx-button class="clearfilter-button" @click="clearFilterAndSorting" icon="clearfilter" hint="Fjern filter og sortering"></dx-button>
          <dx-button
            :disabled="!supplierList.selectedSupplier"
            id="upload-button"
            icon="upload"
            hint="Opdater mapning via excel fil"
          ></dx-button>
        </div>
      </div>
      <dx-data-grid
        ref="dataGrid"
        id="cardProductMappingDataGrid"
        :data-source="datasource"
        height="75vh"
        :allow-column-resizing="true"
        :allow-grouping="true"
        column-resizing-mode="widget"
        :focused-row-key="focusedRowKey"
        @init-new-row="initNewRow"
        @row-removing="rowRemoving">

        <dx-selection mode="single"/>
        <dx-export :enabled="true" fileName="CardProductMapning" />
        <dx-filter-row :visible="true" />
        <dx-header-filter :visible="false" />
        <dx-grouping :auto-expand-all="false" />
        <dx-group-panel :visible="true" />
        <dx-state-storing :enabled="true" type="sessionStorage" storage-key="card-product-mapping-list" />
        <dx-scrolling mode="virtual" />
        <dx-paging :page-size="50" />

        <dx-editing
          :allow-updating="true"
          :allow-deleting="true"
          :allow-adding="true"
          :confirm-delete="false"
          mode="form">
          <dx-form
            :col-count="1"
            label-location="top"
            :showValidationSummary="true">
            <dx-simple-item data-field="supplierId">
              <dx-label text="Leverandør" />
            </dx-simple-item>
            <dx-simple-item data-field="productId">
              <dx-label text="Card product id" />
            </dx-simple-item>
            <dx-simple-item data-field="cardProductName">
              <dx-label text="Card product name" />
            </dx-simple-item>
            <dx-simple-item data-field="brandId">
              <dx-label text="Brand (mastercard or visa)" />
            </dx-simple-item>
            <dx-simple-item data-field="productName">
              <dx-label text="Card org code (NDS)" />
            </dx-simple-item>
            <dx-simple-item data-field="cardOrgCode">
              <dx-label text="Card product name (2)" />
            </dx-simple-item>
            <dx-simple-item data-field="credit" :editor-options="creditEditorOptions">
              <dx-label label="Credit" />
            </dx-simple-item>
          </dx-form>
        </dx-editing>

        <dx-column
          data-field="productId"
          caption="Card product id"
          data-type="string"
          :hiding-priority="8"
          width="20%">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="cardProductName"
          caption="Card product name"
          data-type="string"
          :hiding-priority="7"
          width="20%">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="cardOrgCode"
          caption="Card product name (2)"
          data-type="string"
          :hiding-priority="7"
          :allow-exporting="true"
          width="20%">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="brands"
          :visible="false"
          caption="Brand (Mastercard or Visa)"
          data-type="object"
          edit-cell-template="brandSelector">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="productName"
          caption="Card org code (NDS)"
          data-type="string"
          :hiding-priority="6">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="brandId"
          caption="Brand (Mastercard or Visa)"
          :hiding-priority="5"
          width="20%">
          <dx-grid-lookup
            :data-source="brandList.data"
            value-expr="id"
            display-expr="name"
          />
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="credit"
          :form-item="creditEditorOptions"
          caption="Debit/credit"
          data-type="boolean"
          false-text="Debit"
          true-text="Credit"
          :showEditorAlways="false"
          :hiding-priority="4">
        </dx-column>

        <dx-column
          data-field="supplierId"
          caption="Leverandør"
          :hiding-priority="3"
          :visible="false"
          data-type="number"
          :form-item="supplierFormItem"
          :allow-editing="false">
          <dx-grid-lookup
            :data-source="supplierList.data"
            value-expr="id"
            display-expr="name" />
          <dx-required-rule />
        </dx-column>

        <dx-column
         type="buttons"
         :visible="false" />

        <dx-master-detail
          :enabled="false"
          template="detailTemplate" />

        <template #detailTemplate="{ data: row }">
          <ContentLoaderList v-if="!loadingDataCompleted" />
          <div v-else>
            <DetailView
              :row="row"
              :supplierList="supplierList.data"
              @onEditClick="onEditClick"
              @onDeleteClick="onDeleteClick" />
          </div>
        </template>
      </dx-data-grid>
    </div>
    <Popup v-if="showExcelError"
      :width="600"
      :height="400"
      title="Fejl ved opdatering af card mapnings"
      :visible="showExcelError"
      :content="excelErrors"
      saveButtonText=""
      cancelButtonText="Annuller"
      :showSaveButton="false"
      @onCancel="closeExcelErrorDialog" />
      <Popup v-if="showExcelWarning"
        :width="600"
        :height="200"
        title="Du er ved at overskrive alle card mapnings"
        :visible="showExcelWarning"
        content="Du er ved at overskrive alle card mapnings for den valgte leverandør. Er du sikker på, at du vil fortsætte?"
        saveButtonText="Fortsæt"
        cancelButtonText="Annuller"
        @onCancel="cancelExcelWarningDialog"
        @onSave="closeExcelWarningDialog"
      />
      <Popup v-if="afterExcelSave"
        :width="600"
        :height="200"
        title="Card mapnings er blevet overskrevet"
        :visible="afterExcelSave"
        content="Alle card mapnings er nu blevet overskrevet"
        saveButtonText="Ok"
        :show-cancel-button="false"
        @onSave="afterExcelSaveHandler"
        @onCancel="afterExcelSaveHandler"
      />
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import ContentLoaderList from '@/components/skeletonLoaders/content-loader-list.vue';
import DetailView from '@/modules/masterData/cardProductMapping/components/detail-view.vue';
import { DxFileUploader } from 'devextreme-vue/file-uploader';
import BaseDataGrid from '@/components/common/base-datagrid.vue';
import useDatagrid from '@/modules/masterData/cardProductMapping/composables/datagrid.composable.js';
import useMasterdata from '@/modules/masterData/cardProductMapping/composables/masterdata.composable.js';
import store from '@/store/index';
import Popup from '../../../../components/common/popup.vue';

const supplierFormItem = {
  visible: false,
};

const creditEditorOptions = {
  text: 'Credit',
};

export default {
  name: 'MainCardProductMappingModule',
  extends: BaseDataGrid,
  components: {
    DetailView,
    ContentLoaderList,
    DxFileUploader,
    Popup,
  },
  setup() {
    const {
      datasource,
      getDatasource,
      onInitNewRow,
    } = useDatagrid();

    const {
      supplierList,
      saveSelectedSupplierInStore,
      brandList,
      loadBrandList,
      updateCardProductMappings,
    } = useMasterdata();

    const fileUploader = ref(null);
    const loadingDataCompleted = computed(() => supplierList.loadingCompleted && brandList.loadingCompleted);

    // SupplierChanged eventhandler
    const supplierChanged = () => {
      const supplierId = supplierList.selectedSupplier;

      saveSelectedSupplierInStore(supplierId);
      getDatasource(supplierId);
      loadBrandList(supplierId);
    };

    const showExcelError = ref(false);
    const excelErrors = ref(null);
    const showExcelWarning = ref(false);
    const afterExcelSave = ref(false);
    let fileData = null;

    const onFileSelected = async (e) => {
      fileData = new FormData();
      const file = e.value[0];
      fileData.append('ExcelFile', file);
      fileData.append('SupplierId', supplierList.selectedSupplier);
      showExcelWarning.value = true;
    };

    const closeExcelErrorDialog = () => {
      excelErrors.value = null;
      showExcelError.value = false;
    };

    const showExcelWarningDialog = () => {
      showExcelWarning.value = true;
    };

    const cancelExcelWarningDialog = () => {
      showExcelWarning.value = false;
      fileData = null;
    };

    const closeExcelWarningDialog = async () => {
      store.dispatch('showLoadingPanel', true);
      const result = await updateCardProductMappings(fileData);
      store.dispatch('showLoadingPanel', false);
      if (!result.data.success) {
        excelErrors.value = result.data.messages.map((m) => m.message)
          .join(', ');
        showExcelError.value = true;
      } else {
        afterExcelSave.value = true;
      }
      fileUploader.value.instance.reset(null);
      showExcelWarning.value = false;
    };

    const afterExcelSaveHandler = () => {
      afterExcelSave.value = false;
      const supplierId = supplierList.selectedSupplier;
      getDatasource(supplierId);
    };

    return {
      datasource,
      supplierList,
      supplierChanged,
      saveSelectedSupplierInStore,
      onInitNewRow,
      supplierFormItem,
      loadingDataCompleted,
      brandList,
      creditEditorOptions,
      updateCardProductMappings,
      onFileSelected,
      showExcelError,
      excelErrors,
      showExcelWarning,
      closeExcelErrorDialog,
      closeExcelWarningDialog,
      showExcelWarningDialog,
      cancelExcelWarningDialog,
      fileUploader,
      afterExcelSave,
      afterExcelSaveHandler,
    };
  },
  methods: {
    initNewRow(e) {
      this.onInitNewRow(e, this.supplierList.selectedSupplier);
    },
    rowRemoving(e) {
      this.onRowRemoving(e, `Vil du slette denne cardproduct mapping?<br/><b>${e.data.productId}</b>`, 'Slet cardproduct mapping');
    },
  },
};
</script>

<style lang="scss">
.card-product-mapping-root {
  .clearfilter-button {
    margin-right: 5px;
  }
  .headline-container {
    display: flex;

    .headline-item {
      flex: 0 0 110px;

      h2 {
        margin-top: 0px;
      }
    }

    .subheadline-item {
      padding-top: 11px; font-size: 20px;
    }
  }

  .dx-toolbar-after {
    padding-right: 40px;
  }

  .link:hover {
    cursor: pointer;
  }

  .supplier-container {
    padding: 20px;
    background-color: #FFF;
  }
}
</style>
